import { IconMaximize, IconMinimize, IconPictureInPictureOff, IconX } from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n/provider";
import { cascadeOffset, clampRect, type Plane, type Rect } from "../lib/drill/layers";
import { agPopupIsOpen, layerStack } from "../lib/drill/stack";
import { cx, ICON_STROKE } from "../lib/cx";

/**
 * **Every modal in this console is this component** — docs/24 §7.10, including
 * the form dialog.
 *
 * That is a stacking rule before it is a stylistic one: a fixed ladder (floats
 * 40–49, dialogs 50, docked drills 60–69) assumes a drill never CONTAINS a
 * dialog, and the moment one does — a register behind a picker's ⋯ with its own
 * «new category» — the form opens at 50 beneath a docked register at 60, under
 * that register's own scrim, and reads as a button that does nothing.
 *
 * Raising dialogs above 69 is not the fix: the numbers are a fixed ladder, so it
 * answers this case and leaves the next one. The window CLAIMS a plane on mount
 * and drops it on unmount, so a window opened over another sits above it because
 * it arrived later.
 */

export interface WindowProps {
  title: ReactNode;
  /** A read (a drill panel) may be undocked; a write may not — see `dockable`. */
  plane: Plane;
  onClose: () => void;
  children: ReactNode;
  /**
   * §7.8: **a drill panel is a READ, a write is a dialog.** Only a read offers
   * the undock button, because the question a reader has about a read is
   * comparative — «why did this sub-order settle at a different commission than
   * the one beside it» is answered by holding two of them on one glass. A write
   * that stacks is two forms over one record, and the loser is whichever the
   * operator submits second, silently, because neither can see the other.
   */
  dockable?: boolean;
  /**
   * §7.10: `flush` drops the body's inset, its `gap` and its scrolling, for a
   * child that draws its own measure. A panel of figures wants that padding; **a
   * register does not** — it already draws its own borders, toolbar inset and
   * status bar, so the padding becomes a second frame inside the first, and a
   * scroller around a grid that scrolls its own rows is two scrollbars for one
   * list. A flush body has no height of its own, so a register inside one sets
   * its own and maximising hands it the rest.
   */
  flush?: boolean;
  width?: number;
  height?: number;
}

export function Window({
  title,
  plane,
  onClose,
  children,
  dockable = false,
  flush = false,
  width = 720,
  height = 520,
}: WindowProps) {
  const { i18n } = useT();
  const titleId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const [docked, setDocked] = useState(true);
  const [maximised, setMaximised] = useState(false);
  /**
   * An undocked window opens EXACTLY WHERE A MODAL WOULD and takes a rectangle
   * of its own only at the first drag or resize. Pinning a rectangle at mount
   * means every panel that fills with rows a moment later opens at the height of
   * its own spinner and stays there.
   */
  const [rect, setRect] = useState<Rect | null>(null);
  // Held in a ref so the plane claim below has no dependency on the handler: a
  // claim that was released and retaken on every render would renumber every
  // window on the glass. Written in an effect, never during render.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  // ── the plane, claimed on mount and dropped on unmount ────────────────────
  const [zIndex, setZIndex] = useState(0);
  useLayoutEffect(() => {
    const claim = layerStack.claim({
      plane: docked ? "docked" : plane,
      claimsEscape: () => {
        // A docked window ALWAYS claims. A float claims only while it holds
        // focus. Nothing claims while an AG Grid popup is up.
        if (agPopupIsOpen()) return false;
        if (docked) return true;
        const node = boxRef.current;
        return node !== null && node.contains(document.activeElement);
      },
      onEscape: () => closeRef.current(),
    });
    setZIndex(claim.zIndex);
    return claim.release;
  }, [docked, plane]);

  // The cascade: one step down and across for each float already on the glass.
  const floatsBefore = useRef(0);
  useLayoutEffect(() => {
    floatsBefore.current = Math.max(0, layerStack.count("float") - (docked ? 0 : 1));
  }, [docked]);

  // ── the rectangle, clamped on every move AND on every window resize ───────
  const clamp = useCallback((next: Rect) => {
    return clampRect(next, { width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    if (docked || rect === null) return;
    const onResize = () => setRect((current) => (current === null ? null : clamp(current)));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [docked, rect, clamp]);

  // ── the focus trap ────────────────────────────────────────────────────────
  useEffect(() => {
    // The mount effect focuses the content, not the head: §7.7 puts the window
    // buttons out of the tab order entirely.
    boxRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Bubble phase, so AG Grid has already had the key and `defaultPrevented`
    // says so. Inside a DOCKED window Tab cycles within the box: `aria-modal`
    // tells a screen reader that everything outside the subtree is inert and
    // nothing enforces it, so without this Tab walks behind the scrim into the
    // register.
    if (event.key !== "Tab" || !docked || event.defaultPrevented) return;
    /**
     * `:not([tabindex="-1"])` on EVERY arm, not only on the `[tabindex]` one.
     *
     * Found by `step-up.test.tsx` driving Tab with `user-event`. §7.7 takes the
     * window's own head buttons out of the tab order with `tabIndex={-1}`, but
     * they are still `button:not([disabled])` — so the wrap landed on the close
     * button, which the browser's own tab order excludes, and the NEXT Tab left
     * the window entirely. The trap was returning focus to the one place it
     * could not hold it.
     */
    const focusable = boxRef.current?.querySelectorAll<HTMLElement>(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const startDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (docked || maximised) return;
    const node = boxRef.current;
    if (!node) return;
    const box = node.getBoundingClientRect();
    const grabX = event.clientX - box.left;
    const grabY = event.clientY - box.top;
    const move = (moveEvent: PointerEvent) =>
      setRect(clamp({ x: moveEvent.clientX - grabX, y: moveEvent.clientY - grabY, width: box.width, height: box.height }));
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  const undock = () => {
    const node = boxRef.current;
    if (node) {
      const box = node.getBoundingClientRect();
      const { dx, dy } = cascadeOffset(floatsBefore.current);
      setRect(clamp({ x: box.left + dx, y: box.top + dy, width: box.width, height: box.height }));
    }
    setDocked(false);
  };

  /** Re-docking forgets a dragged rectangle, deliberately. */
  const dock = () => {
    setRect(null);
    setMaximised(false);
    setDocked(true);
  };

  const style: React.CSSProperties = maximised
    ? { zIndex, inset: "8px" }
    : docked || rect === null
      ? { zIndex, width, maxWidth: "calc(100vw - 32px)", height, maxHeight: "calc(100vh - 64px)" }
      : { zIndex, left: rect.x, top: rect.y, width: rect.width, height: rect.height };

  const body = (
    <>
      {/* §9.4 smoke: translucent black in BOTH themes, because dimming a dark
          theme with a dark scrim is the same act. Not blurred.
          §7.4: a press on the scrim closes a docked drill — it is a read
          somebody opened, not a question somebody has to answer. */}
      {docked && (
        <div
          className="scrim fixed inset-0 bg-smoke"
          style={{ zIndex: zIndex - 1 }}
          onPointerDown={onClose}
          aria-hidden
        />
      )}
      <div
        ref={boxRef}
        role="dialog"
        /* §7.4: every drill arrives as a modal — smoke, `aria-modal`, centred,
           the register behind inert. Its first button undocks: the scrim goes,
           the register goes live, and the box becomes a float.
           Failure mode of float-by-default: a centred box over a LIVE register
           looks like a modal and is not, which is a click on the wrong row. */
        aria-modal={docked}
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        /* §7.9's seam: a window is portalled to <body>, so it is nowhere near the
           host form in the DOM — but React propagates events along the COMPONENT
           tree, and in that tree this window is a descendant of the <form> whose
           picker drew the ⋯. Submitting the register's «new category» would
           therefore run the product form's `onSubmit` too, POSTing a half-filled
           product in the same gesture. `preventDefault` does not help: it stops
           the browser's default action, not the bubble. */
        onSubmit={(event) => event.stopPropagation()}
        className={cx(
          "elevated fixed flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-2xl",
          docked && !maximised && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
        )}
        style={style}
      >
        <header
          onPointerDown={startDrag}
          className={cx(
            "flex h-8 shrink-0 items-center gap-2 border-b border-border bg-background px-2",
            !docked && !maximised && "cursor-move",
          )}
        >
          <h2 id={titleId} className="min-w-0 flex-1 truncate text-sm font-semibold">
            {title}
          </h2>
          {/* §7.7: the head is NOT in the tab order. The head is the box's first
              child, so a tabbable close button becomes the first tab stop of
              every drill — ahead of the content the mount effect just focused —
              and a close button that unmounts its own dialog hands focus to
              <body>. Escape is the keyboard route and always works. */}
          {dockable && (
            <button
              type="button"
              tabIndex={-1}
              onClick={docked ? undock : dock}
              aria-label={docked ? i18n._("Undock") : i18n._("Dock")}
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-control-hover hover:text-foreground"
            >
              <IconPictureInPictureOff size={14} stroke={ICON_STROKE} aria-hidden />
            </button>
          )}
          {!docked && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setMaximised((on) => !on)}
              aria-label={maximised ? i18n._("Restore") : i18n._("Maximise")}
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-control-hover hover:text-foreground"
            >
              {maximised ? (
                <IconMinimize size={14} stroke={ICON_STROKE} aria-hidden />
              ) : (
                <IconMaximize size={14} stroke={ICON_STROKE} aria-hidden />
              )}
            </button>
          )}
          <button
            type="button"
            tabIndex={-1}
            onClick={onClose}
            aria-label={i18n._("Close")}
            className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-critical-bg hover:text-critical"
          >
            <IconX size={14} stroke={ICON_STROKE} aria-hidden />
          </button>
        </header>

        <div className={cx("min-h-0 flex-1", flush ? "flex flex-col" : "flex flex-col gap-4 overflow-y-auto p-4")}>
          {children}
        </div>

        {!docked && !maximised && <ResizeGrip onResize={(next) => setRect(clamp(next))} boxRef={boxRef} />}
      </div>
    </>
  );

  return createPortal(body, document.body);
}

function ResizeGrip({
  onResize,
  boxRef,
}: {
  onResize: (rect: Rect) => void;
  boxRef: React.RefObject<HTMLDivElement | null>;
}) {
  const start = (event: React.PointerEvent<HTMLDivElement>) => {
    const node = boxRef.current;
    if (!node) return;
    const box = node.getBoundingClientRect();
    const move = (moveEvent: PointerEvent) =>
      onResize({
        x: box.left,
        y: box.top,
        width: Math.max(320, moveEvent.clientX - box.left),
        height: Math.max(200, moveEvent.clientY - box.top),
      });
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    event.preventDefault();
  };
  return (
    <div
      onPointerDown={start}
      aria-hidden
      className="absolute right-0 bottom-0 size-4 cursor-nwse-resize"
      style={{
        background:
          "linear-gradient(135deg, transparent 50%, var(--color-border-strong) 50%, var(--color-border-strong) 60%, transparent 60%)",
      }}
    />
  );
}
